import type { CSSProperties } from 'react';
import { Panel, Readout, tokens } from '@tiw/ui';
import { useTelemetryStore } from '@/stores/useTelemetryStore';
import type { WSConnectionState } from '@/lib/ws';

/**
 * Telemetry panel — top-right HUD readout grid.
 *
 * Reads exclusively from the telemetry store. The store is fed by the render
 * loop (frame stats) and by the WS client (uplink + connection state). See
 * docs/FRONTEND_PLAN.md.
 */
const DEGRADED_FPS = 30;

/**
 * Overall system health: the combined verdict of the *data* vital sign (WS
 * connection) and the *render* vital sign (FPS). Both must be healthy for
 * NOMINAL — a dead socket OR a stuttering globe is not "fine".
 */
function systemState(
  wsState: WSConnectionState,
  fps: number,
): { label: string; tone: 'good' | 'warn' | 'critical' } {
  if (wsState === 'open') {
    return fps > 0 && fps < DEGRADED_FPS
      ? { label: 'DEGRADED', tone: 'warn' }
      : { label: 'NOMINAL', tone: 'good' };
  }
  if (wsState === 'connecting' || wsState === 'reconnecting') {
    return { label: 'LINKING', tone: 'warn' };
  }
  return { label: 'OFFLINE', tone: 'critical' };
}

export function Telemetry(): JSX.Element {
  const fps = useTelemetryStore((s) => s.fps);
  const drawCalls = useTelemetryStore((s) => s.drawCalls);
  const wsLagMs = useTelemetryStore((s) => s.wsLagMs);
  const eventsPerSec = useTelemetryStore((s) => s.eventsPerSec);
  const wsState = useTelemetryStore((s) => s.wsState);
  const state = systemState(wsState, fps);

  const groupLabel: CSSProperties = {
    color: tokens.color.text.faint,
    fontFamily: tokens.type.family.mono,
    fontSize: tokens.type.size.xs,
    letterSpacing: '0.16em',
    marginBottom: 6,
  };
  const row: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '6px 18px',
  };

  return (
    <Panel
      title="TELEMETRY"
      style={{
        position: 'absolute',
        top: 24,
        right: 24,
        width: 280,
        pointerEvents: 'auto',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <section>
          <div style={groupLabel}>RENDER</div>
          <div style={row}>
            <Readout label="FPS" value={fps.toFixed(0)} unit="hz" tone={fps > 0 && fps < DEGRADED_FPS ? 'warn' : 'good'} />
            <Readout label="DRAW" value={drawCalls.toString()} unit="calls" />
          </div>
        </section>

        <section>
          <div style={groupLabel}>STREAM</div>
          <div style={row}>
            <Readout label="UPLINK" value={wsLagMs.toFixed(1)} unit="ms" tone={wsLagMs > 250 ? 'warn' : 'neutral'} />
            <Readout label="EVENTS" value={eventsPerSec.toFixed(0)} unit="/s" />
          </div>
        </section>

        <div style={{ borderTop: `1px solid ${tokens.color.border.neutral}`, paddingTop: 10 }}>
          <Readout label="STATE" value={state.label} tone={state.tone} />
        </div>
      </div>
    </Panel>
  );
}
